import { FC, ReactNode, type RefObject, useEffect, useRef } from "react"

import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from "body-scroll-lock"
import { t } from "i18next"
import { useClickAway, useKeyPressEvent, useMount, useUnmount } from "react-use"

import { buildCssClassName } from "@/3514/utils"
import { MAIN_PORTAL_ID } from "@/constants"
import CloseIconSvg from "@/svgs/CloseIconSvg"

import Portal from "../portal/Portal"

import "./styles.scss"

interface IProps {
    modalBodyClass?: string
    show: boolean
    enableOutSideClick?: boolean
    disableBodyScroll?: boolean
    headerContent?: ReactNode | null
    closeOnEscapePress?: boolean
    withPortal?: boolean
    portalId?: string
    showCloseIcon?: boolean
    onClose: () => void
    modalOpenBtnLabel?: string
    children?: ReactNode
    modalBodyAriaLabel?: string
}

const Modal: FC<IProps> = ({
    children,
    show,
    onClose,
    modalBodyClass,
    enableOutSideClick = true,
    disableBodyScroll: withDisabledBodyScroll = false,
    headerContent = null,
    closeOnEscapePress = true,
    withPortal = true,
    showCloseIcon = true,
    portalId = MAIN_PORTAL_ID,
    modalOpenBtnLabel = "",
    modalBodyAriaLabel = String()
}) => {
    const modalBodyRef = useRef<HTMLDivElement | null>(null)
    const openModalBtn = document.querySelector(`[aria-label="${modalOpenBtnLabel}"]`) as HTMLButtonElement

    const bodyRef: RefObject<HTMLElement> = useRef<HTMLElement>(document.body)

    useMount((): void => {
        if (!show || !withDisabledBodyScroll || !bodyRef.current) return void 0

        disableBodyScroll(bodyRef.current)

        return void 0
    })

    useUnmount((): void => {
        if (!show || !bodyRef.current || !withDisabledBodyScroll) return

        enableBodyScroll(bodyRef.current)

        clearAllBodyScrollLocks()
    })

    const handleOnClick = () => {
        onClose()
        openModalBtn?.focus()
    }

    useKeyPressEvent("Escape", () => show && closeOnEscapePress && handleOnClick())

    useClickAway(modalBodyRef, () => {
        if (enableOutSideClick && show) {
            handleOnClick()
        }
    })

    useEffect(() => {
        if (show) {
            modalBodyRef.current?.focus()
        }
    }, [show])

    return (
        <>
            {show && (
                <Portal withPortal={withPortal} portalId={portalId}>
                    <div className={buildCssClassName("modal__wrapper", { show })}>
                        <div
                            className={buildCssClassName("modal__body", {
                                show,
                                [modalBodyClass || ""]: modalBodyClass
                            })}
                            role="dialog"
                            ref={modalBodyRef}
                            tabIndex={0}
                            {...(modalBodyAriaLabel && { "aria-label": modalBodyAriaLabel })}
                        >
                            {headerContent ? (
                                <div className="modal__header">
                                    {headerContent}
                                    {showCloseIcon && (
                                        <button
                                            type="button"
                                            aria-label={t("common.close")}
                                            className="modal__header-close-btn"
                                            data-testid="close-btn"
                                            onClick={handleOnClick}
                                        >
                                            <CloseIconSvg />
                                        </button>
                                    )}
                                </div>
                            ) : null}
                            {children}
                        </div>
                    </div>
                </Portal>
            )}
        </>
    )
}

export { type IProps as IModalProps, Modal as default }
